import React, {useCallback, useContext} from 'react';
import {useSelector} from 'react-redux';


import {MessageContext} from 'src/contexts/message-context';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {selectUserDetails} from 'src/modules/profile/user/services/user-page-slice';
import {useUnlockUser} from 'src/modules/profile/user/tabs/user-login/sections/login-actions/unlock-user/useUnlockUser';
import {getUserAuthenticationInfo} from 'src/openapi/admin/apis/UserAuthenticationInfoApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';

type Props = {
    userId: number,
};

export const UnlockUser = ({userId}: Props) => {
    const {authInfo} = useSelector(selectUserDetails);
    const apiDispatch = useAppDispatch();
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const {loginActionSubmit} = useEventTracking();

    const {event, busy: isUnlockBusy} = useUnlockUser({
        userId,
        onSuccess: () => {
            setSuccessMessage('User unlock request has been sent');
            loginActionSubmit({submit: true, success: true, itemAction: 'Unlock User'});
            apiDispatch(getUserAuthenticationInfo({userId: +userId}));

        },
        onFail: (error: string) => {
            loginActionSubmit({submit: true, success: false, itemAction: 'Unlock User'});
            setErrorMessage(error);
        },
    });

    const onClick = useCallback(() => {
        event();
    }, [event]);

    if (!authInfo) {
        return null;
    }

    if (authInfo.error) {
        return (
            <Tooltip
                anchor={({anchorRef, getAnchorProps}) => (
                    <Button
                        ref={anchorRef}
                        {...getAnchorProps()}
                        startIcon={<Icon type={Icons.ERROR}/>}
                        variant="Type.SECONDARY"
                        disabled={true}>
                        Unlock User
                    </Button>
                )}>
                {authInfo.error.message}
            </Tooltip>
        );
    }

    return (
        <Tooltip
            anchor={({anchorRef, getAnchorProps}) => (
                <Button
                    ref={anchorRef}
                    {...getAnchorProps()}
                    busy={isUnlockBusy}
                    disabled={!authInfo?.data?.locked}
                    variant="Type.SECONDARY"
                    onClick={onClick}>
                    Unlock User
                </Button>
            )}>
            The user will be automatically unlocked in 30 minutes
        </Tooltip>
    );
};
